import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MapPin, UserCheck, Mail, Phone, X, Plus, Download, StickyNote, Lock, Unlock } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Chip from '../../../components/ui/Chip';
import Tooltip from '../../../components/ui/Tooltip';
import TextArea from '../../../components/ui/TextArea';
import Select from '../../../components/ui/Select';
import { useParams } from 'react-router-dom';

import { cn } from '../../../utils/cn';



import { fetchPipelineStages, createPipelineStage, updateApplicationPipelineStage, assignApplicationToManager } from '../../../services/applicationsService';
import CandidateUnlockModal from '../../candidate-unlock-system/components/CandidateUnlockModal';
import candidateUnlockService from '../../../services/candidateUnlockService';
import { subscriptionService } from '../../../services/subscriptionService';

const CandidatesManager = () => {
  const { companyId } = useParams();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [filterStage, setFilterStage] = useState('all');
  const [filterJob, setFilterJob] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('applied_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeView, setActiveView] = useState('candidates');
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [unlockedApplications, setUnlockedApplications] = useState(new Set());
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [draggedApplication, setDraggedApplication] = useState(null);
  const [applicationsByStage, setApplicationsByStage] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [companyData, setCompanyData] = useState({ id: companyId });
  const [showAddStageModal, setShowAddStageModal] = useState(false);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('bg-blue-100 text-blue-800');
  const [applications, setApplications] = useState([]);
  const stageColorOptions = [
    { name: 'Blue', color: 'bg-blue-100 text-blue-800' },
    { name: 'Green', color: 'bg-green-100 text-green-800' },
    { name: 'Yellow', color: 'bg-yellow-100 text-yellow-800' },
    { name: 'Red', color: 'bg-red-100 text-red-800' },
    { name: 'Purple', color: 'bg-purple-100 text-purple-800' }
  ];

  useEffect(() => {
    if (companyId) {
      loadCandidates();
      loadPipelineStages();
    }
  }, [companyId, filterStage, filterJob, sortBy, sortOrder]);

  const fetchCompanyApplications = async (companyId, filters) => {
    try {
      // Placeholder implementation - replace with actual API call
      return [];
    } catch (error) {
      console.error('Error fetching company applications:', error);
      return [];
    }
  };

  const loadPipelineData = async (companyId) => {
    try {
      setIsLoading(true);
      // Placeholder implementation - replace with actual API call
      setApplicationsByStage({});
    } catch (error) {
      console.error('Error loading pipeline data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCandidates = async () => {
    try {
      setLoading(true);
      const result = await fetchCompanyApplications(companyId, {
        status: filterStage !== 'all' ? filterStage : undefined,
        jobId: filterJob !== 'all' ? filterJob : undefined,
        sortBy,
        sortOrder
      });

      setCandidates(result || []);
    } catch (error) {
      console.error('Error loading candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPipelineStages = async () => {
    try {
      const result = await fetchPipelineStages(companyId);
      if (result?.success) {
        setPipelineStages(result?.data || []);
      }
    } catch (error) {
      console.error('Error loading pipeline stages:', error);
    }
  };

  const handleAddStage = async () => {
    if (!newStageName?.trim()) return;
    try {
      const result = await createPipelineStage(companyData?.id, {
        name: newStageName,
        color: newStageColor,
        order: pipelineStages?.length
      });
      if (result?.success) {
        setPipelineStages([...pipelineStages, result?.data]);
        setShowAddStageModal(false);
        setNewStageName('');
        setNewStageColor('bg-blue-100 text-blue-800');
      }
    } catch (error) {
      console.error('Failed to add stage:', error);
    }
  };

  const handleDragStart = (e, application) => {
    setDraggedApplication(application);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStageId) => {
    e?.preventDefault();
    if (!draggedApplication) return;
    try {
      const result = await updateApplicationPipelineStage(
        draggedApplication?.id,
        targetStageId
      );
      if (result?.success) {
        await loadPipelineData(companyData?.id);
      }
    } catch (error) {
      console.error('Failed to update stage:', error);
    } finally {
      setDraggedApplication(null);
    }
  };

  const handleAssignManager = async (applicationId, managerId) => {
    try {
      const result = await assignApplicationToManager(applicationId, managerId);
      if (result?.success) {
        await loadPipelineData(companyData?.id);
      }
    } catch (error) {
      console.error('Failed to assign manager:', error);
    }
  };

  const isApplicationUnlocked = (applicationId) => {
    if (['standard', 'premium']?.includes(currentSubscription?.plan)) {
      return true;
    }
    return unlockedApplications?.has(applicationId);
  };

  const handleUnlockClick = (application) => {
    setSelectedApplication(application);
    setShowUnlockModal(true);
  };

  const handleUnlockSuccess = () => {
    loadUnlockedApplications();
  };

  const loadSubscriptionData = async () => {
    const result = await subscriptionService?.getCurrentSubscription(companyData?.id);
    if (result?.success) {
      setCurrentSubscription(result?.data);
    }
  };

  const loadUnlockedApplications = async () => {
    const result = await candidateUnlockService?.getUnlockedCandidates(companyData?.id);
    if (result?.success) {
      const unlockedIds = new Set(result?.data?.map(unlock => unlock?.application_id));
      setUnlockedApplications(unlockedIds);
    }
  };

  const handleViewCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setShowCandidateModal(true);
  };

  const handleUpdateNotes = (candidateId, updatedNotes) => {
    console.log('Notes updated for candidate:', candidateId, updatedNotes);
  };

  const handleUpdateRating = (candidateId, rating, notes) => {
    console.log('Rating updated for candidate:', candidateId, rating, notes);
  };

  useEffect(() => {
    if (companyData?.id) {
      loadSubscriptionData();
      loadUnlockedApplications();
    }
  }, [companyData?.id]);

  const handleUnlockCandidate = async (applicationId) => {
    try {
      const result = await candidateUnlockService?.unlockCandidate(companyData?.id, applicationId);
      if (result?.success) {
        await loadUnlockedApplications();
      }
    } catch (error) {
      console.error('Failed to unlock candidate:', error);
    }
  };

  return (
    <div className="space-y-6">
      <CandidateUnlockModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        companyId={companyData?.id}
        applicationId={selectedApplication?.id}
        application={selectedApplication}
        candidateName={selectedApplication?.user_profiles?.full_name}
        currentPlan={currentSubscription?.plan}
        onUnlockSuccess={handleUnlockSuccess}
      />
      <CandidateProfileModal
        candidate={selectedCandidate}
        isVisible={showCandidateModal}
        onClose={() => setShowCandidateModal(false)}
        onUpdateNotes={handleUpdateNotes}
        onUpdateRating={handleUpdateRating}
        appliedJobs={[]}
      />
      <div className="flex items-center space-x-4 border-b border-border">
        <button
          onClick={() => setActiveView('candidates')}
          className={cn(
            'px-4 py-3 font-medium text-sm border-b-2 transition-colors',
            activeView === 'candidates' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Candidates
        </button>
        <button
          onClick={() => setActiveView('applications')}
          className={cn(
            'px-4 py-3 font-medium text-sm border-b-2 transition-colors',
            activeView === 'applications' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Applications
        </button>
        <button
          onClick={() => {
            setActiveView('pipeline');
            loadPipelineData(companyData?.id);
          }}
          className={cn(
            'px-4 py-3 font-medium text-sm border-b-2 transition-colors',
            activeView === 'pipeline' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Candidate Pipeline
        </button>
      </div>
      {activeView === 'candidates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {candidates?.map((candidate) => {
            const isUnlocked = isApplicationUnlocked(candidate?.id);
            return (
              <div key={candidate?.id} className="relative">
                {!isUnlocked && (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-100/80 to-gray-200/80 backdrop-blur-[2px] rounded-lg flex items-center justify-center z-10">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <Lock className="w-6 h-6 text-gray-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Candidate Locked</p>
                      <Button onClick={() => handleUnlockClick(candidate)} size="sm">
                        <Unlock className="w-4 h-4 mr-2" />
                        Unlock Details
                      </Button>
                    </div>
                  </div>
                )}
                <Card className={cn('p-6', !isUnlocked && 'filter blur-sm pointer-events-none')}>
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      {candidate?.profile?.profilePicture ? (
                        <img
                          src={candidate?.profile?.profilePicture}
                          alt={`${candidate?.profile?.firstName} ${candidate?.profile?.lastName}`}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-xl font-bold text-primary">
                          {candidate?.profile?.firstName?.[0]}{candidate?.profile?.lastName?.[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-foreground truncate">
                        {candidate?.profile?.firstName} {candidate?.profile?.lastName}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate">{candidate?.profile?.headline}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{candidate?.profile?.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Match Score</span>
                      <span className="text-sm font-medium text-primary">{candidate?.applicationData?.matchScore}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Chip variant="secondary" size="sm">{candidate?.applicationData?.status}</Chip>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <Button onClick={() => handleViewCandidate(candidate)} className="w-full" size="sm">
                      View Profile
                    </Button>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      )}
      {activeView === 'applications' && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Applications view coming soon</p>
        </div>
      )}
      {activeView === 'pipeline' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-semibold text-foreground">Candidate Pipeline</h3>
              <p className="text-muted-foreground mt-1">
                Drag and drop applications between stages to track candidate progress
              </p>
            </div>
            <Button onClick={() => setShowAddStageModal(true)} className="inline-flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              Add Stage
            </Button>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4]?.map((i) => (
                <Card key={i} className="p-4 h-96 animate-pulse">
                  <div className="h-6 bg-muted rounded mb-4"></div>
                  <div className="space-y-3">
                    <div className="h-24 bg-muted/50 rounded"></div>
                    <div className="h-24 bg-muted/50 rounded"></div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 touch-pan-x">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 min-w-[320px] md:min-w-0">
                {pipelineStages?.map((stage) => {
                  const stageApplications = applications?.filter(
                    (app) => app?.pipeline_stage_id === stage?.id
                  );

                  return (
                    <Card
                      key={stage?.id}
                      className="flex flex-col h-[500px] md:h-[600px]"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, stage?.id)}
                    >
                      <div className="p-3 md:p-4 border-b border-border">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm md:text-base font-semibold text-foreground">{stage?.name}</h4>
                          <span className={cn('px-2 py-1 rounded-full text-xs font-medium', stage?.color)}>
                            {stageApplications?.length}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 overscroll-contain">
                        {stageApplications?.map((application) => (
                          <div
                            key={application?.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, application)}
                            className="bg-background border border-border rounded-lg p-3 md:p-4 cursor-move hover:shadow-md transition-shadow touch-manipulation active:scale-[0.98]"
                          >
                            <div className="flex items-start space-x-3 mb-3">
                              <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                {application?.user_profiles?.avatar_url ? (
                                  <img
                                    src={application?.user_profiles?.avatar_url}
                                    alt={application?.user_profiles?.full_name}
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="text-sm font-bold text-primary">
                                    {application?.user_profiles?.full_name?.charAt(0) || 'U'}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h5 className="font-semibold text-foreground text-sm truncate">
                                  {application?.user_profiles?.full_name}
                                </h5>
                                <p className="text-xs text-muted-foreground truncate">
                                  {application?.jobs?.title}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {application?.assigned_manager ? (
                                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                                  <UserCheck className="w-3 h-3" />
                                  <span>{application?.assigned_manager?.full_name}</span>
                                </div>
                              ) : (
                                <Select
                                  value={application?.assigned_to || ''}
                                  onChange={(value) => handleAssignManager(application?.id, value)}
                                  options={[
                                    { value: '', label: 'Assign to...' },
                                    ...teamMembers?.map((member) => ({
                                      value: member?.id,
                                      label: member?.full_name
                                    }))
                                  ]}
                                  className="text-xs"
                                  id={`assign-${application?.id}`}
                                  description=""
                                  error=""
                                />
                              )}
                              <div className="flex items-center justify-between">
                                <span
                                  className={cn(
                                    'px-2 py-1 rounded text-xs font-medium',
                                    application?.status === 'pending' && 'bg-blue-100 text-blue-800',
                                    application?.status === 'reviewed' && 'bg-purple-100 text-purple-800',
                                    application?.status === 'interview' && 'bg-yellow-100 text-yellow-800',
                                    application?.status === 'offer' && 'bg-green-100 text-green-800',
                                    application?.status === 'accepted' && 'bg-green-100 text-green-800',
                                    application?.status === 'rejected' && 'bg-red-100 text-red-800'
                                  )}
                                >
                                  {application?.status?.charAt(0)?.toUpperCase() + application?.status?.slice(1)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(application?.applied_at)?.toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {stageApplications?.length === 0 && (
                          <div className="text-center py-8 text-muted-foreground text-sm">
                            No applications in this stage
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          <AddStageModal
            showAddStageModal={showAddStageModal}
            setShowAddStageModal={setShowAddStageModal}
            newStageName={newStageName}
            setNewStageName={setNewStageName}
            newStageColor={newStageColor}
            setNewStageColor={setNewStageColor}
            stageColorOptions={stageColorOptions}
            handleAddStage={handleAddStage}
            cn={cn}
            Tooltip={Tooltip}
          />
        </div>
      )}
    </div>
  );
};

const AddStageModal = ({
  showAddStageModal,
  setShowAddStageModal,
  newStageName,
  setNewStageName,
  newStageColor,
  setNewStageColor,
  stageColorOptions,
  handleAddStage,
  cn,
  Tooltip
}) => (
  <AnimatePresence>
    {showAddStageModal && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
          onClick={() => setShowAddStageModal(false)}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Plus className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-foreground">Add New Stage</h2>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAddStageModal(false)}
                  className="hover:bg-red-50 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form
                onSubmit={(e) => {
                  e?.preventDefault();
                  if (newStageName?.trim()) {
                    handleAddStage();
                  }
                }}
              >
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Stage Name *</label>
                    <Input
                      value={newStageName}
                      onChange={(e) => setNewStageName(e?.target?.value)}
                      placeholder="e.g., Technical Assessment, HR Interview..."
                      className="w-full"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">Choose Color Theme</label>
                    <div className="grid grid-cols-5 gap-2">
                      {stageColorOptions?.map((option) => (
                        <Tooltip key={option?.color} content={option?.name}>
                          <button
                            type="button"
                            onClick={() => setNewStageColor(option?.color)}
                            className={cn(
                              'w-12 h-12 rounded-lg border-2 transition-all duration-200',
                              option?.color,
                              newStageColor === option?.color
                                ? 'ring-2 ring-primary ring-offset-2 scale-110 shadow-md'
                                : 'hover:ring-1 hover:ring-muted-foreground hover:scale-105'
                            )}
                            title={option?.name}
                          />
                        </Tooltip>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Selected: {stageColorOptions?.find((opt) => opt?.color === newStageColor)?.name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 mt-8 pt-6 border-t border-border">
                  <Button type="button" variant="outline" onClick={() => setShowAddStageModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" disabled={!newStageName?.trim()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stage
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const CandidateProfileModal = ({ candidate, isVisible, onClose, onUpdateNotes, onUpdateRating, appliedJobs = [] }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [notes, setNotes] = useState(candidate?.applicationData?.notes || []);
  const [newNote, setNewNote] = useState('');
  const [candidateRating, setCandidateRating] = useState(candidate?.internalRating || 0);

  useEffect(() => {
    setNotes(candidate?.applicationData?.notes || []);
    setCandidateRating(candidate?.internalRating || 0);
  }, [candidate?.id]);

  const handleAddNote = async () => {
    if (!newNote?.trim()) return;
    const note = {
      id: `note_${Date.now()}`,
      text: newNote?.trim(),
      author: 'Current User',
      createdAt: new Date()?.toISOString()
    };
    const updatedNotes = [note, ...notes];
    setNotes(updatedNotes);
    setNewNote('');
    onUpdateNotes?.(candidate?.id, updatedNotes);
  };

  const handleUpdateRating = async (newRating) => {
    setCandidateRating(newRating);
    onUpdateRating?.(candidate?.id, newRating, '');
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed inset-6 z-50 overflow-hidden"
          >
            <div className="w-full h-full bg-background border border-border rounded-xl shadow-2xl flex flex-col">
              <div className="p-8 border-b border-border bg-gradient-to-r from-primary/5 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center border-2 border-primary/20 shadow-sm">
                      {candidate?.profile?.profilePicture ? (
                        <img
                          src={candidate?.profile?.profilePicture}
                          alt={`${candidate?.profile?.firstName} ${candidate?.profile?.lastName}`}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl font-bold text-primary">
                          {candidate?.profile?.firstName?.[0]}{candidate?.profile?.lastName?.[0]}
                        </span>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <h2 className="text-3xl font-bold text-foreground mb-2">
                          {candidate?.profile?.firstName} {candidate?.profile?.lastName}
                        </h2>
                        <p className="text-lg text-muted-foreground">{candidate?.profile?.headline}</p>
                      </div>
                      <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4" />
                          <span>{candidate?.profile?.location}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4" />
                          <span>{candidate?.profile?.email}</span>
                        </div>
                        {candidate?.profile?.phone && (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4" />
                            <span>{candidate?.profile?.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                      {candidate?.applicationData?.matchScore}% match
                    </div>
                    <Button size="sm" className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white">
                      <Download className="w-4 h-4 mr-2" />
                      Download Resume
                    </Button>
                    <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-red-50 hover:text-red-600">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="border-b border-border bg-background px-8">
                <nav className="flex">
                  {[
                    { id: 'profile', label: 'Full Profile', icon: UserCheck },
                    { id: 'rating', label: 'Internal Rating', icon: Star, showRating: true },
                    { id: 'notes', label: 'Notes', icon: StickyNote, count: notes?.length || 0 }
                  ]?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={cn(
                        'flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors',
                        activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                      )}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab?.label}</span>
                      {tab?.showRating && (
                        <div className="flex items-center ml-1">
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full font-medium">
                            {candidateRating}/10
                          </span>
                        </div>
                      )}
                      {tab?.count !== undefined && tab?.count > 0 && (
                        <span
                          className={cn(
                            'ml-1 px-2 py-0.5 rounded-full text-xs font-medium',
                            activeTab === tab?.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {tab?.count}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>
              <div className="flex-1 overflow-y-auto p-8">
                {activeTab === 'profile' && (
                  <div className="space-y-12 max-w-4xl">
                    {candidate?.profile?.summary && (
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-foreground">Professional Summary</h3>
                        <div className="bg-muted/20 rounded-lg p-6">
                          <p className="text-muted-foreground leading-relaxed text-base">{candidate?.profile?.summary}</p>
                        </div>
                      </div>
                    )}
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-foreground">Skills</h3>
                      {candidate?.skills?.technical && (
                        <div className="space-y-4">
                          <h4 className="text-lg font-medium text-foreground">Technical Skills</h4>
                          <div className="flex flex-wrap gap-3">
                            {candidate?.skills?.technical?.map((skill, index) => (
                              <Chip key={index} variant="primary" size="sm">
                                {skill}
                              </Chip>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === 'rating' && (
                  <div className="space-y-8 max-w-4xl">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-full mb-4">
                        <Star className="w-8 h-8 text-yellow-600" />
                      </div>
                      <h3 className="text-2xl font-semibold text-foreground mb-2">Internal Candidate Rating</h3>
                      <p className="text-muted-foreground max-w-2xl mx-auto">
                        Rate {candidate?.profile?.firstName} {candidate?.profile?.lastName} based on their overall fit.
                      </p>
                    </div>
                    <Card className="p-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
                      <div className="text-center">
                        <div className="mb-6">
                          <div className="text-5xl font-bold text-foreground mb-2">
                            {candidateRating?.toFixed(1)}
                            <span className="text-2xl text-muted-foreground ml-1">/10</span>
                          </div>
                          <div className="flex items-center justify-center space-x-2 mb-4">
                            {[...Array(10)]?.map((_, index) => {
                              const starValue = index + 1;
                              const isFullStar = starValue <= Math.floor(candidateRating);
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => handleUpdateRating(starValue)}
                                  className="relative w-8 h-8 hover:scale-105 transition-transform"
                                >
                                  <Star
                                    className={cn(
                                      'w-8 h-8 transition-colors',
                                      isFullStar ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
                                    )}
                                  />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                )}
                {activeTab === 'notes' && (
                  <div className="space-y-8 max-w-4xl">
                    <div className="bg-muted/20 rounded-lg p-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <StickyNote className="w-5 h-5 text-primary" />
                        <h3 className="text-lg font-semibold text-foreground">Add New Note</h3>
                      </div>
                      <TextArea
                        value={newNote}
                        onChange={(e) => setNewNote(e?.target?.value)}
                        placeholder="Add a note about this candidate..."
                        rows={4}
                        className="w-full mb-4"
                        label=""
                        description=""
                        error=""
                        id="new-note"
                      />
                      <Button onClick={handleAddNote} disabled={!newNote?.trim()}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Note
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {notes?.map((note) => (
                        <Card key={note?.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-foreground mb-2">{note?.text}</p>
                              <p className="text-xs text-muted-foreground">
                                {note?.author} • {new Date(note?.createdAt)?.toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </Card>
                      ))}
                      {notes?.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">No notes yet</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CandidatesManager;